import { MAX_HOUSEHOLD_RECIPES, QuotaExceededError, RecipeCeilingError } from '@platewell/core';
import { useIsOwner, useRecipeQuota } from '@platewell/query';
import { useNavigate } from '@tanstack/react-router';
import { ApiError } from '../lib/api.js';

export interface QuotaRefusal {
  /** The plan's cap, when the refusal knew it. */
  limit: number | null;
  /** Slots still free. Zero means the household is already full. */
  remaining: number | null;
  /** Full at `MAX_HOUSEHOLD_RECIPES`, which no plan lifts, so there is nothing to sell. */
  ceiling: boolean;
}

/**
 * Recognises the two shapes a full household arrives in.
 *
 * `QuotaExceededError` is the client's own refusal, thrown before anything is written and
 * the only half that works offline. `ApiError('quota_exceeded')` is the server refusing a
 * whole batch up front, counted from the D1 projection — which trails the document, so it
 * can fire on an import the client thought had room. Both mean the same thing to the
 * person reading the screen, and neither should ever reach them as a raw message.
 */
export function quotaRefusal(cause: unknown): QuotaRefusal | null {
  if (cause instanceof QuotaExceededError) {
    return { limit: cause.limit, remaining: 0, ceiling: cause instanceof RecipeCeilingError };
  }
  if (cause instanceof ApiError && cause.code === 'quota_exceeded') {
    return { limit: null, remaining: cause.remaining, ceiling: false };
  }
  if (cause instanceof ApiError && cause.code === 'recipe_ceiling') {
    return { limit: MAX_HOUSEHOLD_RECIPES, remaining: 0, ceiling: true };
  }
  return null;
}

export interface UpgradeNoticeProps {
  refusal: QuotaRefusal;
  /**
   * Completes "Subscribe to …" — e.g. `import the rest`. Phrased per call site so the
   * prompt names the thing the user was actually just stopped from doing.
   */
  action: string;
}

/**
 * What a blocked action says instead of an error.
 *
 * The plan banner already explains the cap in the abstract; this is the same fact at the
 * moment it costs the user something, so the subscribe button has to be right here rather
 * than a scroll away. Members who cannot buy are told who can — offering them a button
 * that 402s is worse than offering none.
 */
export function UpgradeNotice({ refusal, action }: UpgradeNoticeProps) {
  const isOwner = useIsOwner();
  const quota = useRecipeQuota();
  const navigate = useNavigate();

  if (refusal.ceiling) {
    return (
      <div className="upgrade-notice" role="status">
        <div className="upgrade-notice-text">
          <p className="upgrade-notice-headline">
            This household has reached the {MAX_HOUSEHOLD_RECIPES.toLocaleString('en-US')}-recipe
            maximum.
          </p>
          <p className="hint">Delete some recipes to make room for new ones.</p>
        </div>
      </div>
    );
  }

  // The server's refusal carries no cap, only what is left, so fall back to the document's.
  const limit = refusal.limit ?? quota.limit;
  const remaining = refusal.remaining;

  const headline =
    remaining != null && remaining > 0
      ? `That’s more than the free plan has room for — ${remaining} recipe ${
          remaining === 1 ? 'slot' : 'slots'
        } left.`
      : limit != null
        ? `You’ve reached the ${limit}-recipe free plan.`
        : 'You’ve reached the free plan’s recipe limit.';

  return (
    <div className="upgrade-notice" role="status">
      <div className="upgrade-notice-text">
        <p className="upgrade-notice-headline">{headline}</p>
        <p className="hint">
          {isOwner
            ? `Subscribe to ${action} — everyone in the household is covered.`
            : `Ask an owner of this household to subscribe to ${action}.`}
        </p>
      </div>
      {isOwner && (
        <button className="btn primary" onClick={() => void navigate({ to: '/subscribe' })}>
          Subscribe
        </button>
      )}
    </div>
  );
}
